import { useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import fp from "lodash/fp"

import type { DateRange } from "types/date.types"

import type { DateRangeKey, DateRangeSource } from "features/store/dateRangeSlice"
import {
  DATE_RANGE_KEYS,
  dateRangeKeyFor,
  selectDateRanges,
  updateDateRange,
} from "features/store/dateRangeSlice"
import { formatSearchParams } from "helpers/utils/dateUtils"
import { selectPlotsLoadingByKey, selectArePlotsLoading } from "helpers/utils/plots"

interface DateRangeTools {
  dateRange: DateRange | null
  dispatchDateRange: (dateRange: DateRange | null) => void
  isDateRangeValid: boolean
  searchParams: string
}

interface UseDateRangeReturn {
  getDateRangeTools: (source: DateRangeSource) => DateRangeTools
  getPlotLoadingState: (key: string) => boolean
  arePlotsLoading: boolean
}

const isDateRangeDirty = (
  searchParams: URLSearchParams,
  dateRange: DateRange | null,
): boolean => {
  const range = searchParams.get("range")
  const fromDate = searchParams.get("fromDate")
  const toDate = searchParams.get("toDate")
  const zoom = searchParams.get("zoom")

  // For custom ranges, compare all params except zoom (custom never has zoom in params)
  if (range && range === "custom") {
    return !fp.isEqual(
      { range, fromDate, toDate },
      {
        range: dateRange?.range,
        fromDate: dateRange?.fromDate,
        toDate: dateRange?.toDate,
      },
    )
  }

  // For zoom mode, compare range, fromDate, toDate, and zoom
  if (zoom === "true" && dateRange?.zoom) {
    return !fp.isEqual(
      { range, fromDate, toDate, zoom: "true" },
      {
        range: dateRange?.range,
        fromDate: dateRange?.fromDate,
        toDate: dateRange?.toDate,
        zoom: "true",
      },
    )
  }

  // For normal ranges, just compare range
  if (range && range !== "custom" && range !== dateRange?.range) {
    return true
  }

  return false
}

const useDateRange = (): UseDateRangeReturn => {
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const dateRanges = useSelector(selectDateRanges)

  const plotsLoadingByKey = useSelector(selectPlotsLoadingByKey, fp.isEqual)
  const arePlotsLoading = useSelector(selectArePlotsLoading)

  const getPlotLoadingState = useCallback(
    (key: string) => {
      return plotsLoadingByKey[key] || false
    },
    [plotsLoadingByKey],
  )

  // Kept apart from the tools below so a dispatcher never changes identity: the
  // refresh intervals depend on it and would reset on every range change.
  const dispatchers = useMemo(
    () =>
      Object.fromEntries(
        DATE_RANGE_KEYS.map((key) => [
          key,
          (dateRange: DateRange | null) => {
            dispatch(updateDateRange({ key, dateRange }))
          },
        ]),
      ) as Record<DateRangeKey, DateRangeTools["dispatchDateRange"]>,
    [dispatch],
  )

  const dateRangeTools = useMemo(
    () =>
      Object.fromEntries(
        DATE_RANGE_KEYS.map((key) => {
          const dateRange = dateRanges[key]
          return [
            key,
            {
              dateRange,
              dispatchDateRange: dispatchers[key],
              isDateRangeValid: !!dateRange && !isDateRangeDirty(searchParams, dateRange),
              searchParams: formatSearchParams(dateRange),
            },
          ]
        }),
      ) as Record<DateRangeKey, DateRangeTools>,
    [dateRanges, dispatchers, searchParams],
  )

  const getDateRangeTools = useCallback(
    (source: DateRangeSource) => dateRangeTools[dateRangeKeyFor(source)],
    [dateRangeTools],
  )

  return {
    getDateRangeTools,
    getPlotLoadingState,
    arePlotsLoading,
  }
}

export default useDateRange
